import React, { useEffect, useState, useRef } from "react";

import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SystemUpdateAltRoundedIcon from "@mui/icons-material/SystemUpdateAltRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";

import firebase from "../firebase";
import { primaryTint } from "../surfaceTints";

const DEFAULT_MESSAGE =
    "Nodiant is being updated. Please refresh in a few minutes to load the latest version.";
const UPDATE_CHECK_INTERVAL = 60 * 1000;
const UPDATE_MESSAGE =
    "A new version of Nodiant is available. Refresh to load the latest version.";

function getLoadedMainScript() {
    const mainScript = Array.from(document.scripts).find((script) =>
        /\/static\/js\/main\.[^/]+\.chunk\.js$/.test(script.src),
    );

    return mainScript ? new URL(mainScript.src).pathname : null;
}

// Blocking overlay driven by the /appStatus node. Setting
// { maintenance: true, message: "..." } in the database shows it on every
// connected client, including signed-out pages; clearing the flag makes any
// client that saw the overlay reload itself to pick up the new deploy.
export default function MaintenanceOverlay() {
    const [status, setStatus] = useState(null);
    const [updateAvailable, setUpdateAvailable] = useState(false);

    const sawMaintenanceRef = useRef(false);
    const loadedManifestRef = useRef(null);
    const availableVersionRef = useRef(null);
    const dismissedVersionRef = useRef(null);
    const updateCheckInProgressRef = useRef(false);

    useEffect(() => {
        const ref = firebase.database().ref("appStatus");

        const handleSnapshot = ref.on(
            "value",
            (snapshot) => {
                const data = snapshot.val();

                if (data && data.maintenance) {
                    sawMaintenanceRef.current = true;
                    setStatus(data);
                } else if (sawMaintenanceRef.current) {
                    // Flag just cleared: the new version is live, load it
                    window.location.reload();
                } else {
                    setStatus(null);
                }
            },
            (error) => {
                console.error("Unable to get app status from Firebase", error);
            },
        );

        return () => ref.off("value", handleSnapshot);
    }, []);

    useEffect(() => {
        if (process.env.NODE_ENV !== "production") {
            return;
        }

        const loadedMainScript = getLoadedMainScript();

        if (!loadedMainScript) {
            return;
        }

        let isActive = true;

        const checkForUpdate = async () => {
            if (updateCheckInProgressRef.current) {
                return;
            }

            updateCheckInProgressRef.current = true;

            try {
                const response = await fetch(
                    `/asset-manifest.json?updateCheck=${Date.now()}`,
                    {
                        cache: "no-store",
                        headers: {
                            Accept: "application/json",
                        },
                    },
                );

                if (!response.ok) {
                    return;
                }

                const manifest = await response.json();
                const latestMainScript = manifest.files?.["main.js"];
                const manifestVersion = JSON.stringify(
                    manifest.entrypoints || manifest.files,
                );

                if (
                    !latestMainScript ||
                    !manifestVersion ||
                    !isActive
                ) {
                    return;
                }

                const latestMainPath = new URL(
                    latestMainScript,
                    window.location.origin,
                ).pathname;

                if (loadedManifestRef.current === null) {
                    loadedManifestRef.current = manifestVersion;
                }

                if (
                    (latestMainPath !== loadedMainScript ||
                        manifestVersion !== loadedManifestRef.current) &&
                    manifestVersion !== dismissedVersionRef.current
                ) {
                    availableVersionRef.current = manifestVersion;
                    setUpdateAvailable(true);
                }
            } catch (error) {
                // A temporary network failure should not interrupt the user.
            } finally {
                updateCheckInProgressRef.current = false;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkForUpdate();
            }
        };

        checkForUpdate();

        const intervalId = window.setInterval(
            checkForUpdate,
            UPDATE_CHECK_INTERVAL,
        );

        window.addEventListener("focus", checkForUpdate);
        document.addEventListener(
            "visibilitychange",
            handleVisibilityChange,
        );

        return () => {
            isActive = false;
            window.clearInterval(intervalId);
            window.removeEventListener("focus", checkForUpdate);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
        };
    }, []);

    const handleUpdateDismiss = () => {
        dismissedVersionRef.current = availableVersionRef.current;
        setUpdateAvailable(false);
    };

    if (!status && !updateAvailable) {
        return null;
    }

    const maintenanceIsActive = Boolean(status);
    const title = maintenanceIsActive
        ? "Update in progress"
        : "New version available";
    const message = maintenanceIsActive
        ? status?.message || DEFAULT_MESSAGE
        : UPDATE_MESSAGE;

    return (
        <Dialog
            open
            fullWidth
            maxWidth="xs"
            disableEscapeKeyDown={maintenanceIsActive}
            onClose={maintenanceIsActive ? undefined : handleUpdateDismiss}
            aria-labelledby="maintenance-overlay-title"
            aria-describedby="maintenance-overlay-message"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                                ? "rgba(4, 8, 6, 0.68)"
                                : "rgba(12, 25, 17, 0.56)",
                        backdropFilter: "blur(5px)",
                    },
                },
                paper: {
                    sx: {
                        m: { xs: 2, sm: 3 },
                        borderRadius: 4,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                                ? "0 24px 70px rgba(0, 0, 0, 0.6)"
                                : "0 24px 70px rgba(12, 25, 17, 0.25)",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    px: { xs: 2.25, sm: 3 },
                    py: { xs: 2.25, sm: 2.75 },
                    backgroundColor: "background.paper",
                }}
            >
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 2.5,
                        color: "primary.main",
                        backgroundColor: primaryTint,
                    }}
                >
                    {maintenanceIsActive ? (
                        <ConstructionOutlinedIcon />
                    ) : (
                        <SystemUpdateAltRoundedIcon />
                    )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            display: "block",
                            mb: 0.25,
                            color: "primary.main",
                            fontWeight: 900,
                            lineHeight: 1,
                            letterSpacing: "0.08em",
                        }}
                    >
                        {maintenanceIsActive
                            ? "System maintenance"
                            : "Nodiant update"}
                    </Typography>
                    <Typography
                        id="maintenance-overlay-title"
                        variant="h5"
                        component="h2"
                        sx={{ fontWeight: 800, lineHeight: 1.15 }}
                    >
                        {title}
                    </Typography>
                </Box>
            </Box>

            {maintenanceIsActive && (
                <LinearProgress
                    aria-label="Update in progress"
                    sx={{ height: 3 }}
                />
            )}

            <DialogContent
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        p: { xs: 2, sm: 2.25 },
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 0.75, fontWeight: 800 }}
                    >
                        {maintenanceIsActive
                            ? "Thanks for your patience"
                            : "Ready when you are"}
                    </Typography>
                    <Typography
                        id="maintenance-overlay-message"
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.65 }}
                    >
                        {message}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    gap: 0.5,
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    backgroundColor: "background.paper",
                }}
            >
                {!maintenanceIsActive && (
                    <Button color="inherit" onClick={handleUpdateDismiss}>
                        Later
                    </Button>
                )}
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<RefreshRoundedIcon />}
                    onClick={() => window.location.reload()}
                >
                    Refresh
                </Button>
            </DialogActions>
        </Dialog>
    );
}
